/**
 * Approvals → Blocked by policy → Re-check (Deivydas 2026-10-19, superadmin
 * only). Zoye looks again, with today's code, at the tickets whose refund was
 * blocked: she replies to nobody and moves nothing until Apply. Each ticket
 * shows what she would refund now — or why not — and Apply executes exactly
 * that through the same path as Execute.
 *
 * The same pass runs every night (Deivydas 2026-11-03): it applies what the
 * gate now passes (never a refund over the approval line — that waits for
 * Apply here), clears what is settled, and moves what waits for the
 * customer's yes to Awaiting customer. The panel shows the tab's tickets and
 * the last night's totals, and "Run nightly now" queues one on the worker.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { ApiError, apiGet, apiPost } from '../../api/client';
import { Button, Card, CardHeader, Table, Td, Th } from '../../components/ui/primitives';
import { ZendeskTicketRef } from '../../components/ticket/ZendeskTicketRef';
import { formatRelative, formatUsd } from '../../lib/format';

interface MoneyRow {
  id: string;
  type?: string;
  amountCents: number | null;
  orderId: string | null;
  percent: number | null;
  reasons?: string[];
  /** Over the approval line: only Apply here runs it, never the nightly pass. */
  needsSignature?: boolean;
}

interface NightlyPass {
  at: string;
  tickets?: number;
  rechecked?: number;
  refunded?: number;
  refundedCents?: number;
  cleared?: number;
  awaiting?: number;
  stillBlocked?: number;
  failed?: number;
  /** Approvals → Failed rows cleared as settled, and those left (2026-11-03). */
  failedCleared?: number;
  failedLeft?: number;
}

interface RecheckTicket {
  ticketId: string;
  externalId: string;
  brand: string;
  customer: string | null;
  /** Every row waits only for the customer: Approvals → Awaiting customer. */
  awaitingCustomer?: boolean;
  blocked: Array<MoneyRow & { createdAt: string }>;
  recheck: null | {
    runId: string | null;
    state: 'queued' | 'running' | 'done' | 'skipped' | 'failed';
    note: string | null;
    at: string;
    draft: string | null;
    escalationReason: string | null;
    /** Why nothing would be refunded, from the run's facts. */
    reason: string | null;
    alreadyRefunded: boolean;
    /** The blocked rows' orders refunded in full already (Apply clears those rows). */
    refundedOrderIds: string[];
    openOrderIds: string[];
    ready: MoneyRow[];
    stillBlocked: MoneyRow[];
  };
}

interface ApplyResult {
  runId: string;
  actionId?: string;
  orderId?: string | null;
  amountCents?: number | null;
  outcome: string;
  note?: string;
}

const REASON_WORDS: Record<string, string> = {
  'policy:no_explicit_customer_consent': 'no yes from the customer',
  'policy:save_offer_with_cancellation': 'save offer beside a cancellation',
  'policy:refund_block_active': 'refund block on the order',
  'policy:outside_guarantee_window': 'outside the guarantee window',
};

const reasonText = (reasons: string[] = []) =>
  reasons
    .filter((reason) => reason !== 'recheck:awaiting_customer')
    .map((reason) => REASON_WORDS[reason] ?? reason.replace(/^policy:/, '').replace(/_/g, ' '))
    .join(', ');

const moneyLine = (row: MoneyRow) =>
  `${formatUsd(row.amountCents)} on ${row.orderId ?? 'the order'}${row.percent !== null ? ` (${row.percent}%)` : ''}`;

export function RecheckPanel({ onDone, awaitingCustomer = false }: { onDone: () => void; awaitingCustomer?: boolean }) {
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<ApplyResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Keeps polling for a minute after Start, while intake creates the runs.
  const [pollUntil, setPollUntil] = useState(0);

  const list = useQuery({
    queryKey: ['approvals', 'recheck'],
    queryFn: () => apiGet<{ tickets: RecheckTicket[]; nightly: NightlyPass | null }>('/api/approvals/recheck'),
    // While a re-check runs, the list follows it.
    refetchInterval: (query) =>
      query.state.data?.tickets.some((t) => t.recheck?.state === 'running' || t.recheck?.state === 'queued') ||
      Date.now() < pollUntil
        ? 5000
        : false,
  });
  // This tab's tickets: Awaiting customer, or the rest of Blocked by policy.
  const tickets = useMemo(
    () => (list.data?.tickets ?? []).filter((t) => (t.awaitingCustomer ?? false) === awaitingCustomer),
    [list.data, awaitingCustomer],
  );
  const nightly = list.data?.nightly ?? null;
  const [nightlyQueued, setNightlyQueued] = useState(false);
  const runNightly = useMutation({
    mutationFn: () => apiPost('/api/approvals/recheck/nightly', {}),
    onSuccess: () => {
      setError(null);
      setNightlyQueued(true);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not queue the nightly re-check.'),
  });

  const start = useMutation({
    mutationFn: (ticketIds: string[]) => apiPost('/api/approvals/recheck/start', { ticketIds }),
    onSuccess: () => {
      setError(null);
      // The runs are created a moment after the request returns.
      setPollUntil(Date.now() + 60_000);
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['approvals', 'recheck'] }), 1500);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not start the re-check.'),
  });

  const apply = useMutation({
    mutationFn: (runIds: string[]) => apiPost<{ results: ApplyResult[] }>('/api/approvals/recheck/apply', { runIds }),
    onSuccess: (data) => {
      setResults(data.results);
      setPicked(new Set());
      setError(null);
      onDone();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not apply.'),
  });

  const toggle = (ticketId: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });

  const chosen = tickets.filter((t) => picked.has(t.ticketId));
  const done = chosen.filter((t) => t.recheck?.state === 'done' && t.recheck.runId);
  const refundable = done.filter((t) => (t.recheck?.ready.length ?? 0) > 0);
  // Already refunded by a person: Apply only clears the blocked rows.
  const clearable = done.filter((t) => t.recheck?.ready.length === 0 && (t.recheck.refundedOrderIds?.length ?? 0) > 0);
  const applicable = [...refundable, ...clearable];
  const applyCents = refundable.reduce(
    (sum, t) => sum + (t.recheck?.ready ?? []).reduce((s, r) => s + (r.amountCents ?? 0), 0),
    0,
  );
  const allPicked = tickets.length > 0 && tickets.every((t) => picked.has(t.ticketId));

  const onApply = () => {
    const count = refundable.reduce((n, t) => n + (t.recheck?.ready.length ?? 0), 0);
    const parts = [
      count > 0 ? `refund ${formatUsd(applyCents)} in ${count} refund(s) now — this moves real money and cannot be undone from here` : null,
      clearable.length > 0 ? `clear ${clearable.length} already-refunded ticket(s) from Blocked` : null,
    ].filter(Boolean);
    if (!window.confirm(`Apply: ${parts.join('; and ')}?`)) return;
    apply.mutate(applicable.map((t) => t.recheck!.runId!));
  };

  if (list.isLoading || tickets.length === 0) return null;

  return (
    <Card className="mb-4">
      <CardHeader
        title="Re-check blocked refunds"
        meta="Zoye looks at each ticket again with today's rules. Nothing is sent to the customer and nothing moves until you apply."
      />
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={chosen.length === 0 || start.isPending}
          onClick={() => start.mutate(chosen.map((t) => t.ticketId))}
        >
          <RefreshCw size={13} strokeWidth={2} aria-hidden /> Re-check selected ({chosen.length})
        </Button>
        <Button size="sm" variant="primary" disabled={applicable.length === 0 || apply.isPending} onClick={onApply}>
          Apply selected ({formatUsd(applyCents)}{clearable.length > 0 ? ` + clear ${clearable.length}` : ''})
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={runNightly.isPending || nightlyQueued}
          onClick={() => {
            if (
              window.confirm(
                'Run the nightly re-check now? It re-checks every blocked ticket, applies the refunds the gate passes (up to the approval line — real money), clears what is settled (Failed too) and moves what waits for a yes to Awaiting customer.',
              )
            ) {
              runNightly.mutate();
            }
          }}
        >
          {nightlyQueued ? 'Nightly re-check queued' : 'Run nightly now'}
        </Button>
        {list.isFetching ? <span className="text-2xs text-ink-400">updating…</span> : null}
        {error ? <span className="text-xs text-stop-700">{error}</span> : null}
      </div>
      {nightly ? (
        <div className="border-b border-line px-4 py-2 text-2xs text-ink-500">
          Last nightly re-check {formatRelative(nightly.at)}: {nightly.rechecked ?? 0} of {nightly.tickets ?? 0} tickets
          re-checked, {nightly.refunded ?? 0} refunded ({formatUsd(nightly.refundedCents ?? 0)}), {nightly.cleared ?? 0}{' '}
          cleared, {nightly.awaiting ?? 0} to Awaiting customer, {nightly.stillBlocked ?? 0} left blocked
          {nightly.failed ? `, ${nightly.failed} failed` : ''}
          {typeof nightly.failedCleared === 'number'
            ? `; Failed: ${nightly.failedCleared} cleared as settled, ${nightly.failedLeft ?? 0} left for a person`
            : ''}
          .
        </div>
      ) : null}
      <Table>
        <thead>
          <tr>
            <Th className="w-8">
              <input
                type="checkbox"
                aria-label="Select all"
                checked={allPicked}
                onChange={() => setPicked(allPicked ? new Set() : new Set(tickets.map((t) => t.ticketId)))}
              />
            </Th>
            <Th>Ticket</Th>
            <Th>Blocked</Th>
            <Th>Zoye now</Th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket) => (
            <tr key={ticket.ticketId} className="align-top">
              <Td>
                <input
                  type="checkbox"
                  aria-label={`Select ticket ${ticket.externalId}`}
                  checked={picked.has(ticket.ticketId)}
                  onChange={() => toggle(ticket.ticketId)}
                />
              </Td>
              <Td className="whitespace-nowrap text-xs">
                <ZendeskTicketRef externalId={ticket.externalId} className="font-mono" />
                <Link
                  to={`/tickets/${ticket.ticketId}`}
                  className="ml-2 text-2xs text-ink-500 underline underline-offset-2 hover:text-accent-700"
                >
                  open in Zoye
                </Link>
                <div className="text-2xs text-ink-400">
                  {ticket.customer ?? '—'} · {ticket.brand}
                </div>
              </Td>
              <Td className="text-xs text-ink-700">
                {ticket.blocked.map((row) => (
                  <div key={row.id}>
                    {moneyLine(row)} <span className="text-ink-400">— {reasonText(row.reasons)}</span>
                  </div>
                ))}
              </Td>
              <Td className="text-xs">
                <RecheckResult recheck={ticket.recheck} />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {results ? (
        <div className="border-t border-line px-4 py-3 text-xs">
          <div className="mb-1 font-semibold text-ink-900">Applied</div>
          {results.map((result, index) => (
            <div key={`${result.runId}-${result.actionId ?? index}`} className="text-ink-700">
              {result.orderId ? `${formatUsd(result.amountCents ?? null)} on ${result.orderId}: ` : ''}
              <span className={result.outcome === 'refunded' ? 'text-ok-700' : 'text-stop-700'}>
                {result.outcome.replace(/_/g, ' ')}
              </span>
              {result.note ? <span className="text-ink-400"> — {result.note}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

function RecheckResult({ recheck }: { recheck: RecheckTicket['recheck'] }) {
  if (!recheck) return <span className="text-ink-400">not re-checked yet</span>;
  if (recheck.state === 'queued') {
    return <span className="text-ink-500">queued — waiting its turn (since {formatRelative(recheck.at)})</span>;
  }
  if (recheck.state === 'running') {
    return <span className="text-ink-500">checking… (started {formatRelative(recheck.at)})</span>;
  }
  if (recheck.state === 'skipped') return <span className="text-ink-500">skipped — {recheck.note}</span>;
  if (recheck.state === 'failed') return <span className="text-stop-700">failed — {recheck.note}</span>;
  return (
    <div>
      {recheck.ready.map((row) => (
        <div key={row.id} className="text-ok-700">
          Would refund {moneyLine(row)}
          {row.needsSignature ? <span className="text-ink-500"> — over the approval line: Apply here</span> : null}
        </div>
      ))}
      {recheck.stillBlocked.map((row) => (
        <div key={row.id} className="text-stop-700">
          Still blocked: {moneyLine(row)} — {reasonText(row.reasons)}
        </div>
      ))}
      {recheck.ready.length === 0 && recheck.stillBlocked.length === 0 ? (
        recheck.alreadyRefunded ? (
          <div className="text-ok-700">
            Nothing to refund: {recheck.reason}. Apply clears it from Blocked.
          </div>
        ) : (recheck.refundedOrderIds?.length ?? 0) > 0 ? (
          <div>
            <div className="text-ok-700">
              {recheck.refundedOrderIds.join(', ')} refunded in full — Apply clears those rows.
            </div>
            <div className="text-stop-700">{recheck.openOrderIds.join(', ')} not refunded — a person decides.</div>
          </div>
        ) : (
          <div className="text-ink-500">
            Would refund nothing now
            {recheck.reason ? ` — ${recheck.reason}` : recheck.escalationReason ? ` — ${recheck.escalationReason}` : ''}
          </div>
        )
      ) : null}
      <div className="text-2xs text-ink-400">checked {formatRelative(recheck.at)}</div>
    </div>
  );
}
